import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { LOADING_CONTEXT } from '../interceptors/loading-context.token';

/** Modules the assistant supports vs is still being trained on (display labels). */
export interface ChatbotModuleCapabilities {
  available: string[];
  inTraining: string[];
}

/** AI usage for the panel's meter. cap/remaining null = unlimited (platform). */
export interface ChatbotCreditUsage {
  cap: number | null;
  used: number;
  remaining: number | null;
  enabled: boolean;
}

export interface ChatbotSessionUsage {
  used: number;
  cap: number;
}

export interface ChatbotUsage {
  credits: ChatbotCreditUsage;
  sessions: ChatbotSessionUsage;
  // Whether a new turn would be accepted right now. When false, the panel
  // disables the composer and surfaces blockedReason instead of sending.
  canSend: boolean;
  blockedReason: string | null;
}

export interface ChatbotMessageRequest {
  message: string;
  conversationId?: string | null;
  history?: ChatbotHistoryItem[];
  options?: ChatbotRequestOptions;
  module?: string | null;
  route?: string | null;
  portalKind?: 'admin' | 'member';
  entityContext?: ChatbotEntityContext | null;
  clientContext?: ChatbotClientContext | null;
}

export interface ChatbotHistoryItem {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatbotRequestOptions {
  includeCitations?: boolean;
  includeSuggestions?: boolean;
}

export interface ChatbotMessageResponse {
  conversationId: string;
  message: ChatbotMessage;
  citations?: ChatbotCitation[] | null;
  navigation?: ChatbotNavigation[] | null;
  suggestions?: string[] | null;
  slotFillGroups?: ChatbotSlotFillGroup[] | null;
  toolRequired: boolean;
}

export interface ChatbotSlotFillOption {
  label: string;
  value: string;
}

export interface ChatbotSlotFillGroup {
  fieldKey: string;
  label: string;
  selectMode: 'single' | 'multi';
  options: ChatbotSlotFillOption[];
}

export interface ChatbotMessage {
  role: 'assistant';
  content: string;
  // Persisted ai_message.id for the assistant turn. Frontend reuses it as
  // ChatMessage.id so PATCH /messages/{msgId}/selections addresses the same
  // row that was just inserted. Missing on unpersisted responses (rare).
  id?: string | null;
}

export interface ChatbotCitation {
  title: string;
  sourceFile?: string | null;
  section?: string | null;
  sourcePath?: string | null;
}

export interface ChatbotNavigation {
  label: string;
  route: string;
  requiresModule?: string | null;
  kind?: string | null;
  prefill?: Record<string, string> | null;
  prompt?: string | null;
}

export interface ChatbotEntityContext {
  entityType: string;
  entityId: string | null;
  displayName?: string | null;
}

export interface ChatbotClientContext {
  contextVariant?: 'list' | 'detail' | 'selected-dialog' | null;
  launcherSource?: 'inline' | 'header' | 'unknown' | null;
  capabilities?: Record<string, boolean> | null;
}

export interface ChatbotSessionSummary {
  id: string;
  title?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChatbotStoredMessage {
  id: string;
  // Backend ai_message.role is uppercase ("USER"/"ASSISTANT"); the panel's
  // hydrateStoredMessage normalizes to the ChatRole lowercase union.
  role: string;
  content?: string | null;
  createdAt: string;
  // Per-turn snapshot persisted to ai_message.metadata. Null on user rows
  // and on rows written before V059. The panel uses this to re-render
  // chips/nav/suggestions + restore the user's prior chip selections.
  metadata?: ChatbotStoredMessageMetadata | null;
}

export interface ChatbotStoredMessageMetadata {
  citations?: ChatbotCitation[] | null;
  navigation?: ChatbotNavigation[] | null;
  suggestions?: string[] | null;
  slotFillGroups?: ChatbotSlotFillGroup[] | null;
  toolRequired?: boolean;
  // Outer key = fieldKey, value = selected option codes. Mirrors the
  // panel's slotSelections[messageId] shape.
  selections?: Record<string, string[]> | null;
}

@Injectable({ providedIn: 'root' })
export class ChatbotService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api/chat';
  private readonly sessionsUrl = '/api/chatbot/sessions';

  sendMessage(request: ChatbotMessageRequest): Observable<ChatbotMessageResponse> {
    const context = new HttpContext().set(LOADING_CONTEXT, 'none');
    return this.http.post<ChatbotMessageResponse>(this.apiUrl, request, { context });
  }

  /** Modules the assistant supports vs is still being trained on (display labels). */
  getCapabilities(): Observable<ChatbotModuleCapabilities> {
    const context = new HttpContext().set(LOADING_CONTEXT, 'none');
    return this.http.get<ChatbotModuleCapabilities>('/api/chatbot/capabilities', { context });
  }

  /** Current AI credit + chat-session usage for the panel's usage meter. */
  getUsage(): Observable<ChatbotUsage> {
    const context = new HttpContext().set(LOADING_CONTEXT, 'none');
    return this.http.get<ChatbotUsage>('/api/chatbot/usage', { context });
  }

  listSessions(): Observable<ChatbotSessionSummary[]> {
    const context = new HttpContext().set(LOADING_CONTEXT, 'none');
    return this.http.get<ChatbotSessionSummary[]>(this.sessionsUrl, { context });
  }

  getSessionMessages(id: string): Observable<ChatbotStoredMessage[]> {
    const context = new HttpContext().set(LOADING_CONTEXT, 'none');
    return this.http.get<ChatbotStoredMessage[]>(`${this.sessionsUrl}/${id}/messages`, { context });
  }

  deleteSession(id: string): Observable<void> {
    const context = new HttpContext().set(LOADING_CONTEXT, 'none');
    return this.http.delete<void>(`${this.sessionsUrl}/${id}`, { context });
  }

  // Full-replace semantics: the body is the complete selections map for the
  // message. Backend overwrites whatever was stored. No loading spinner
  // (LOADING_CONTEXT 'none') so chip clicks stay snappy.
  updateMessageSelections(
    conversationId: string,
    messageId: string,
    selections: Record<string, string[]>,
  ): Observable<void> {
    const context = new HttpContext().set(LOADING_CONTEXT, 'none');
    return this.http.patch<void>(
      `${this.sessionsUrl}/${conversationId}/messages/${messageId}/selections`,
      { selections },
      { context },
    );
  }

  // Best-effort 1-token completion against the configured LLM to force a
  // local runner (Ollama, LM Studio) to load the model into VRAM. Backend
  // swallows errors and returns 202 either way — frontend treats this as
  // fire-and-forget. Reuses the AI controller's existing endpoint (same
  // mechanism the member-import wizard uses on step 1).
  warmupLlm(): Observable<void> {
    const context = new HttpContext().set(LOADING_CONTEXT, 'none');
    return this.http.post<void>('/api/ai/warmup', null, { context });
  }
}
